interface PseoHreflangAlternate {
  hrefLang: string;
  href: string;
}

const ROBONEO_SITE_BASE_URL = 'https://www.roboneo.com';

const PSEO_PT_HREFLANG_SLUGS = [
  'ai-advertising-generator',
  'ai-audio-denoise',
  'ai-object-removal',
  'ai-product-photography-agent',
  'blog/ai-video-clip-generator',
  'blog/ai-video-generator-online',
  'blog/best-ai-music-video-generator',
  'blog/best-ai-text-video-generator',
  'blog/frames-to-video',
  'blog/free-ai-music-video-generator',
  'blog/gpt-image-2',
  'blog/happy-horse-1-0',
  'blog/how-to-agent-teams',
  'blog/how-to-turn-cool-photos-into-a-video',
  'blog/logo-video-creator',
  'blog/make-videos-from-photos-online',
  'blog/meta-ai-image-generator',
  'blog/text-to-video-online',
  'motion-sync',
  'seedance-2-0',
  'seedance2',
  'video-memes-generator',
  'video-watermark-removal',
];

const PSEO_PT_HREFLANG_SLUG_SET = new Set(PSEO_PT_HREFLANG_SLUGS);
const PSEO_MULTI_LOCALE_HREFLANG_SLUG_SET = new Set([
  'ai-cutout',
  'ai-enhance',
  'ai-eraser',
  'ai-watermark',
]);

const PSEO_MULTI_LOCALE_HREFLANG_CODES = ['de', 'en', 'es', 'ja', 'ko', 'pt', 'th'];

// 只取 canonical 的路径做自指校验，避免 pre/localhost/www 域名差异挡住已确认的 Essio 页面。
function getCanonicalPathname(url?: string) {
  if (!url) return '';
  try {
    return new URL(url).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
}

// 根据当前 Essio 路径识别是否属于已确认的 en/pt 互返 SEO 页面组。
function getPseoPtHreflangSlug(pathKey: string) {
  const normalizedPathKey = pathKey.replace(/^\/+|\/+$/g, '');
  const pathWithoutPtLocale = normalizedPathKey.replace(/^pt\//, '');

  if (!PSEO_PT_HREFLANG_SLUG_SET.has(pathWithoutPtLocale)) {
    return undefined;
  }

  if (normalizedPathKey === pathWithoutPtLocale || normalizedPathKey === `pt/${pathWithoutPtLocale}`) {
    return pathWithoutPtLocale;
  }

  return undefined;
}

// 根据当前 Essio 路径识别是否属于已确认的多语种 PSEO AI 工具页面组。
function getPseoMultiLocaleHreflangConfig(pathKey: string) {
  const normalizedPathKey = pathKey.replace(/^\/+|\/+$/g, '');
  const [locale, ...slugParts] = normalizedPathKey.split('/');
  const slug = slugParts.join('/');

  if (!locale || !PSEO_MULTI_LOCALE_HREFLANG_CODES.includes(locale) || !PSEO_MULTI_LOCALE_HREFLANG_SLUG_SET.has(slug)) {
    return undefined;
  }

  return { locale, slug };
}

// 仅在 canonical 与当前 en/pt 页面自指一致时输出 hreflang，避免给 canonical 异常页面硬加 alternate。
export function getPseoHreflangAlternates(pathKey: string, canonical?: string): PseoHreflangAlternate[] {
  const multiLocaleConfig = getPseoMultiLocaleHreflangConfig(pathKey);
  if (multiLocaleConfig) {
    const expectedCanonicalPathname = `/${multiLocaleConfig.locale}/${multiLocaleConfig.slug}`;

    if (getCanonicalPathname(canonical) !== expectedCanonicalPathname) {
      return [];
    }

    return [
      ...PSEO_MULTI_LOCALE_HREFLANG_CODES.map((hrefLang) => ({
        hrefLang,
        href: `${ROBONEO_SITE_BASE_URL}/${hrefLang}/${multiLocaleConfig.slug}`,
      })),
      { hrefLang: 'x-default', href: `${ROBONEO_SITE_BASE_URL}/en/${multiLocaleConfig.slug}` },
    ];
  }

  const slug = getPseoPtHreflangSlug(pathKey);
  if (!slug) return [];

  const normalizedPathKey = pathKey.replace(/^\/+|\/+$/g, '');
  const enHref = `${ROBONEO_SITE_BASE_URL}/${slug}`;
  const ptHref = `${ROBONEO_SITE_BASE_URL}/pt/${slug}`;
  const expectedCanonicalPathname = normalizedPathKey.startsWith('pt/') ? `/pt/${slug}` : `/${slug}`;

  if (getCanonicalPathname(canonical) !== expectedCanonicalPathname) {
    return [];
  }

  return [
    { hrefLang: 'en', href: enHref },
    { hrefLang: 'pt', href: ptHref },
    { hrefLang: 'x-default', href: enHref },
  ];
}
